<script>
import AuthLayout from "@/Layouts/AuthLayout.vue";

export default {
    layout: AuthLayout,
};
</script>

<script setup>
import { Head, Link, useForm } from "@inertiajs/vue3";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import BrandInput from "@/Components/Brand/BrandInput.vue";
import BrandPasswordInput from "@/Components/Brand/BrandPasswordInput.vue";
import { ref, watch } from "vue";
import { debounce } from "lodash";

const form = useForm({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    password_confirmation: "",
    terms: false,
});

const emailSuggestion = ref("");
const commonMistypedDomains = {
    "gamil.com": "gmail.com",
    "gnail.com": "gmail.com",
    "gmaill.com": "gmail.com",
    "yahooo.com": "yahoo.com",
    "yahoo.vom": "yahoo.com",
    "yahoo.con": "yahoo.com",
    "hotmail.con": "hotmail.com",
};

const checkEmailForTypos = () => {
    const emailParts = form.email.split("@");
    if (emailParts.length === 2) {
        const domain = emailParts[1];
        if (commonMistypedDomains[domain]) {
            emailSuggestion.value = `${emailParts[0]}@${commonMistypedDomains[domain]}`;
        } else if (domain.includes("gmail") && domain !== "gmail.com") {
            emailSuggestion.value = `${emailParts[0]}@gmail.com`;
        } else {
            emailSuggestion.value = "";
        }
    }
};

const updateEmail = () => {
    form.email = emailSuggestion.value;
    emailSuggestion.value = "";
};

watch(() => form.email, debounce(checkEmailForTypos, 500));

const submit = () => {
    form.post(route("register"), {
        onFinish: () => form.reset("password", "password_confirmation"),
    });
};
</script>

<template>
    <Head title="Sign Up" />

    <div class="flex w-full flex-col items-center">
        <!-- Headline over the panel art (mobile) / logo + headline over the photo band (desktop) -->
        <div class="flex w-full items-center gap-6 pl-[18px] md:w-auto md:pl-0">
            <span class="hidden shrink-0 md:block" data-environment-logo>
                <img
                    :src="asset('images/brand/logo-mark.png')"
                    alt="Gift Hero"
                    class="block size-[75px] object-contain"
                />
            </span>
            <h1
                class="max-w-[190px] font-brand-display text-[30px] font-black italic uppercase leading-none tracking-[-0.02em] text-white md:max-w-none md:text-[32px]"
            >
                Ready to be the
                <br class="max-md:hidden" />
                ultimate gift hero?
            </h1>
        </div>

        <form
            class="mt-[38px] w-full max-w-[408px] rounded-brand bg-white p-[18px] shadow-[0px_30px_55px_0px_rgba(0,0,0,0.25)] sm:p-9 md:mt-7"
            @submit.prevent="submit"
        >
            <div class="space-y-[22px]">
                <div>
                    <label
                        for="first_name"
                        class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                        >First Name</label
                    >
                    <BrandInput
                        id="first_name"
                        v-model="form.first_name"
                        type="text"
                        required
                        autofocus
                        autocomplete="given-name"
                    />
                    <p
                        v-if="form.errors.first_name"
                        class="mt-1.5 text-[13px] text-brand-red"
                    >
                        {{ form.errors.first_name }}
                    </p>
                </div>

                <div>
                    <label
                        for="last_name"
                        class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                        >Last Name</label
                    >
                    <BrandInput
                        id="last_name"
                        v-model="form.last_name"
                        type="text"
                        required
                        autocomplete="family-name"
                    />
                    <p
                        v-if="form.errors.last_name"
                        class="mt-1.5 text-[13px] text-brand-red"
                    >
                        {{ form.errors.last_name }}
                    </p>
                </div>

                <div>
                    <label
                        for="email"
                        class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                        >E-Mail Address</label
                    >
                    <BrandInput
                        id="email"
                        v-model="form.email"
                        type="email"
                        required
                        autocomplete="email"
                    />
                    <p
                        v-if="form.errors.email"
                        class="mt-1.5 text-[13px] text-brand-red"
                    >
                        {{ form.errors.email }}
                    </p>
                    <p
                        v-if="emailSuggestion"
                        class="mt-1.5 text-[13px] text-brand-red"
                    >
                        Did you mean
                        <a
                            href="#"
                            class="underline"
                            @click.prevent="updateEmail"
                            >{{ emailSuggestion }}</a
                        >?
                    </p>
                </div>

                <div>
                    <label
                        for="password"
                        class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                        >Password</label
                    >
                    <BrandPasswordInput
                        id="password"
                        v-model="form.password"
                        required
                        autocomplete="new-password"
                    />
                    <p class="mt-1.5 text-[11px] leading-4 text-[#3c3938]">
                        Password must be at least 8 characters and include an
                        uppercase letter, a number, and a special character
                    </p>
                    <p
                        v-if="form.errors.password"
                        class="mt-1.5 text-[13px] text-brand-red"
                    >
                        {{ form.errors.password }}
                    </p>
                </div>

                <div>
                    <label
                        for="password_confirmation"
                        class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                        >Confirm Password</label
                    >
                    <BrandPasswordInput
                        id="password_confirmation"
                        v-model="form.password_confirmation"
                        required
                        autocomplete="new-password"
                    />
                    <p
                        v-if="form.errors.password_confirmation"
                        class="mt-1.5 text-[13px] text-brand-red"
                    >
                        {{ form.errors.password_confirmation }}
                    </p>
                </div>
            </div>

            <div
                v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature"
                class="mt-6"
            >
                <label class="flex items-start gap-3.5" for="terms">
                    <input
                        id="terms"
                        v-model="form.terms"
                        name="terms"
                        type="checkbox"
                        required
                        class="mt-0.5 size-[18px] shrink-0 rounded border-[#a79e9b] text-brand-navy focus:ring-brand-navy"
                    />
                    <span
                        class="font-brand text-[12px] font-medium leading-[18px] text-[#3c3938]"
                    >
                        I confirm I am over 13 years of age and I agree to the
                        <a
                            target="_blank"
                            :href="route('terms.show')"
                            class="text-[#3c3938] underline"
                            >Terms of Service</a
                        >
                        and
                        <a
                            target="_blank"
                            :href="route('policy.show')"
                            class="text-[#3c3938] underline"
                            >Privacy Policy</a
                        >
                    </span>
                </label>
                <p
                    v-if="form.errors.terms"
                    class="mt-1.5 text-[13px] text-brand-red"
                >
                    {{ form.errors.terms }}
                </p>
            </div>

            <BrandButton
                type="submit"
                class="mt-8 h-11 w-full text-[15px]"
                :class="{ 'pointer-events-none opacity-60': form.processing }"
                :disabled="form.processing"
            >
                Register your Account
            </BrandButton>
        </form>

        <p class="mt-7 font-brand text-[15px] text-white md:mt-6">
            <span class="font-semibold">Already Registered?</span>
            <Link
                :href="route('login')"
                class="ml-1.5 !text-white underline hover:opacity-80"
                >Sign In</Link
            >
        </p>
    </div>
</template>
